import * as React from 'react';
import { HuaweiAppGallerySettings, HuaweiAppStoreState } from '../types';
import {
    antGrey,
    releaseSettingsDescriptionsStyle,
    releaseStepDetailsContainerModuleBackgroundStyle,
    releaseStepDetailsContainerModuleStyle,
    releaseStepModuleTitleStyle,
    runwayColors,
} from './styles/globalStyles';
import { EditOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import { huaweiAppIsReleased } from '../helpers';
import PermissionedActionButton from './HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../helpers/aclHelpers';
import { Nullable, ReleaseComponentProps } from '../typescriptHelpers';
import Space from 'antd/lib/space';
import TooltipInfoIcon from './helperComponents/TooltipInfoIcon';
import {
    selectAppStoreDefaults,
    selectIsSubmitAutomationInactiveMissingAppStoreDefaults,
} from '../selectors/selectors';
import { useSelector } from 'react-redux';
import { RootState } from '../reducers/reducers';
import HuaweiAppGalleryReleaseSettingsDescriptions from '@/components/appSettings/HuaweiAppGalleryReleaseSettingsDescriptions';

interface HuaweiAppStoreReleaseSettingsProps extends ReleaseComponentProps {
    releaseSettings?: HuaweiAppGallerySettings;
    huaweiAppGalleryState?: HuaweiAppStoreState;
    storeUrl: string;
    onShowAppStoreReleaseSettings: () => void;
    autoStagedRolloutAutomationId: Nullable<string>;
}

const HuaweiAppStoreReleaseSettings: React.FC<HuaweiAppStoreReleaseSettingsProps> = (
    props: HuaweiAppStoreReleaseSettingsProps,
) => {
    const editReleaseSettingsDisabled =
        props.huaweiAppGalleryState ? huaweiAppIsReleased(props.huaweiAppGalleryState) : false;

    const onClickEditReleaseDefaults = () => {
        props.onShowAppStoreReleaseSettings();
    };

    return (
        <div
            style={{
                ...releaseStepDetailsContainerModuleStyle,
                ...releaseStepDetailsContainerModuleBackgroundStyle,
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <Space>
                    <Text style={releaseStepModuleTitleStyle}>Release settings</Text>
                    <TooltipInfoIcon
                        tooltipContent={'These settings will be applied to your release during submission.'}
                    />
                </Space>
                <PermissionedActionButton
                    actionType={RunwayUserAction.UAUpdateAppSettings}
                    icon={<EditOutlined style={{ color: runwayColors.info }} disabled={editReleaseSettingsDisabled} />}
                    onClick={onClickEditReleaseDefaults}
                    releaseId={props.releaseId}
                >
                    Edit release settings
                </PermissionedActionButton>
            </div>
            <HuaweiAppStoreReleaseSettingsBase {...props} />
        </div>
    );
};

export const HuaweiAppStoreReleaseSettingsBase: React.FC<HuaweiAppStoreReleaseSettingsProps> = (props) => {
    const isSubmitAutomationDisabledMissingDefaults = useSelector((state: RootState) => {
        if (props.releaseId) {
            return selectIsSubmitAutomationInactiveMissingAppStoreDefaults(state, props.appId, props.releaseId);
        } else {
            return false;
        }
    });

    const appStoreDefaults = useSelector((state: RootState) => selectAppStoreDefaults(state, props.appId));

    return (
        <HuaweiAppGalleryReleaseSettingsDescriptions
            orgId={props.orgId}
            appId={props.appId}
            releaseId={props.releaseId}
            autoStagedRolloutAutomationId={props.autoStagedRolloutAutomationId}
            labelStyle={{ ...releaseSettingsDescriptionsStyle, marginTop: 12 }}
            contentStyle={{ color: antGrey.grey7, marginTop: 12 }}
            isSubmitAutomationDisabledMissingDefaults={isSubmitAutomationDisabledMissingDefaults ?? false}
            appDefaultSettings={appStoreDefaults as HuaweiAppGallerySettings}
            releaseSettings={props.releaseSettings}
        />
    );
};

export default HuaweiAppStoreReleaseSettings;
